import React from "react";
import { Box, Dialog, DialogTitle, Tooltip } from "@mui/material";
import { IconButton } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Cancel01Icon,
  LinkSquare02Icon,
} from "@hugeicons-pro/core-stroke-rounded";

interface DocumentViewerDialogProps {
  /** Remote document to show. Nothing renders until this is non-empty. */
  url: string;
  open: boolean;
  /** Names the frame for assistive tech; falls back to a generic label. */
  title?: string;
  /**
   * Fires once per dismissal: the close button, the backdrop, or Escape.
   * Callers run mutations from here, so it must not fire twice.
   */
  onClose: () => void;
}

/**
 * Shows a remote document (a hosted bank verification page, a transfer
 * receipt) in an iframe over the current screen.
 *
 * The iframe is the point: these are third-party pages the app cannot render
 * itself, which is why this is not `AttachmentViewer`. It is the only iframe in
 * the client.
 */
const DocumentViewerDialog: React.FC<DocumentViewerDialogProps> = ({
  url,
  open,
  title,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-label={title ?? t("Document")}
      slotProps={{
        paper: {
          sx: {
            width: 480,
            // The old fixed 900 overflowed a laptop viewport, and the frame
            // was clipped rather than shrunk.
            height: "min(900px, 90vh)",
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "var(--space-2)",
          flexShrink: 0,
          padding: "var(--space-5)",
        }}
      >
        {/* Gated on the same `url` as the frame below: with nothing to show,
            the link would point at about:blank, or at the current page if the
            url arrived null rather than empty. */}
        {url ? (
          <Tooltip title={t("Open in new tab")}>
            <IconButton
              aria-label={t("Open in new tab")}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <HugeiconsIcon icon={LinkSquare02Icon} size={18} />
            </IconButton>
          </Tooltip>
        ) : null}
        <IconButton aria-label={t("Close")} onClick={onClose}>
          <HugeiconsIcon icon={Cancel01Icon} size={18} />
        </IconButton>
      </DialogTitle>

      {/* Deliberately not DialogContent: the theme pads it, and a document
          frame should meet the panel edges. `flex: 1` is what gives the frame
          its height, rather than the 100% that used to overflow the header. */}
      {url ? (
        <Box
          component="iframe"
          src={url}
          title={title ?? t("Document")}
          sx={{ flex: 1, width: "100%", border: "none", display: "block" }}
        />
      ) : null}
    </Dialog>
  );
};

export default DocumentViewerDialog;
